import { useState, useEffect, useRef } from 'react';
import type { Request, TabState } from '../../types';
import { getFiltered, matchesSub } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeByOppId, sumUniqueByOpp, isReqAged, ageSeverity, formatReqAge, splitUserSCNames, getRequestURL } from '../../utils/opportunities';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { RangeSlider } from '../ui/RangeSlider';
import { OfferingChart, bucketSolution } from '../charts/OfferingChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useRequests } from '../../context/RequestsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-1085" data-mp="1gw9d4a4n5jy2">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    requests
  } = useRequests();
  const all = getFiltered('sd', state, requests);
  const ip = all.filter(r => r.req_status === 'In Progress');
  const cls = all.filter(r => r.req_status === 'Closed');
  const aged = ip.filter(r => isReqAged(r));
  const scs = new Set(ip.flatMap(r => splitUserSCNames(r.user_sc_name))).size;
  const oppGnarr = sumUniqueByOpp(all, r => r.opp_gnarr);
  const oppGnarrTotal = sumUniqueByOpp(all, r => r.opp_gnarr_grand_total);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeByOppId(requests.filter(r => {
    if (r.opp_adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (!matchesSub(state.sub, r.req_sub_region)) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!r.opp_close_q) return false;
      const [yr, qt] = r.opp_close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, r) => s + (r.opp_gnarr || 0), 0);
  const wonGnarrTotal = wonDeals.reduce((s, r) => s + (r.opp_gnarr_grand_total || 0), 0);
  const wonCount = wonDeals.length;

  // Merge requests by opp_id: combine SC names, keep all requests for age links
  interface MergedRequest extends Request {
    mergedSc: string;
    displayStatus: string;
    allRequests: Request[];
  }
  const requestMap = new Map<string, MergedRequest>();
  for (const r of all) {
    const existing = requestMap.get(r.opp_id);
    if (existing) {
      const scNames = new Set([...existing.mergedSc.split(',').map(s => s.trim()).filter(Boolean), ...r.user_sc_name.split(',').map(s => s.trim()).filter(Boolean)]);
      existing.mergedSc = [...scNames].join(', ');
      existing.allRequests.push(r);
      if (!existing.displayStatus && r.opp_status) existing.displayStatus = r.opp_status;
    } else {
      requestMap.set(r.opp_id, {
        ...r,
        mergedSc: r.user_sc_name,
        displayStatus: r.opp_status,
        allRequests: [r]
      });
    }
  }
  const mergedRequests = [...requestMap.values()];
  const [searchId, setSearchId] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [searchSc, setSearchSc] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const subDropdownRef = useRef<HTMLDivElement>(null);

  // Derived ranges from data
  const ages = mergedRequests.map(r => r.req_age ?? 0);
  const gnarrs = mergedRequests.map(r => r.opp_gnarr ?? 0);
  const dataAgeMin = Math.min(...ages);
  const dataAgeMax = Math.max(...ages);
  const dataGnarrMin = Math.min(...gnarrs);
  const dataGnarrMax = Math.max(...gnarrs);
  const [ageMin, setAgeMin] = useState(dataAgeMin);
  const [ageMax, setAgeMax] = useState(dataAgeMax);
  const [gnarrMin, setGnarrMin] = useState(dataGnarrMin);
  const [gnarrMax, setGnarrMax] = useState(dataGnarrMax);

  // deals loads asynchronously (empty on first render), so the useState initializers above
  // capture Math.min/max(...[]) === ±Infinity and never resync — once real data arrives,
  // pull the range in once so the age/gnarr filters aren't stuck rejecting every row.
  // Also clamp the slider values whenever the data range changes to prevent overflow.
  useEffect(() => {
    if (ageMin === Infinity && Number.isFinite(dataAgeMin)) {
      setAgeMin(dataAgeMin);
      setAgeMax(dataAgeMax);
    } else if (Number.isFinite(dataAgeMin) && Number.isFinite(dataAgeMax)) {
      // Clamp existing values to new data range
      setAgeMin(prev => Math.max(dataAgeMin, Math.min(prev, dataAgeMax)));
      setAgeMax(prev => Math.min(dataAgeMax, Math.max(prev, dataAgeMin)));
    }
  }, [dataAgeMin, dataAgeMax]);
  useEffect(() => {
    if (gnarrMin === Infinity && Number.isFinite(dataGnarrMin)) {
      setGnarrMin(dataGnarrMin);
      setGnarrMax(dataGnarrMax);
    } else if (Number.isFinite(dataGnarrMin) && Number.isFinite(dataGnarrMax)) {
      // Clamp existing values to new data range
      setGnarrMin(prev => Math.max(dataGnarrMin, Math.min(prev, dataGnarrMax)));
      setGnarrMax(prev => Math.min(dataGnarrMax, Math.max(prev, dataGnarrMin)));
    }
  }, [dataGnarrMin, dataGnarrMax]);
  const [selectedCloseQs, setSelectedCloseQs] = useState<string[]>([]);
  const [closeQDropdownOpen, setCloseQDropdownOpen] = useState(false);
  const closeQDropdownRef = useRef<HTMLDivElement>(null);
  const [offeringTop, setOfferingTop] = useState(8);
  const [offeringTotal, setOfferingTotal] = useState(1);
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [othersLabels, setOthersLabels] = useState<string[]>([]);
  const handleSolutionClick = (name: string) => {
    setSelectedSolution(prev => prev === name ? null : name);
  };
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);
  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target as Node)) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [regionDropdownOpen]);
  useEffect(() => {
    if (!subDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (subDropdownRef.current && !subDropdownRef.current.contains(e.target as Node)) {
        setSubDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [subDropdownOpen]);
  useEffect(() => {
    if (!closeQDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (closeQDropdownRef.current && !closeQDropdownRef.current.contains(e.target as Node)) {
        setCloseQDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [closeQDropdownOpen]);
  const allStatuses = [...new Set(mergedRequests.map(r => r.displayStatus).filter(Boolean))].sort();
  const allRegions = [...new Set(mergedRequests.map(r => r.opp_region).filter(Boolean))].sort();

  // Build region ↔ sub mappings from data
  const regionToSubs = new Map<string, Set<string>>();
  const subToRegion = new Map<string, string>();
  for (const r of mergedRequests) {
    if (!r.opp_region || !r.req_sub_region) continue;
    if (!regionToSubs.has(r.opp_region)) regionToSubs.set(r.opp_region, new Set());
    regionToSubs.get(r.opp_region)!.add(r.req_sub_region);
    if (!subToRegion.has(r.req_sub_region)) subToRegion.set(r.req_sub_region, r.opp_region);
  }

  // Subs available: if regions are selected, only show subs under those regions; otherwise all
  const availableSubs = selectedRegions.length > 0 ? [...new Set(selectedRegions.flatMap(r => [...(regionToSubs.get(r) ?? [])]))].sort() : [...new Set(mergedRequests.map(r => r.req_sub_region).filter(Boolean))].sort();

  // Auto-select region when a sub is selected and no region is active;
  // auto-clear region when deselecting a sub leaves no subs under that region.
  const autoSelectedRegionRef = useRef<string | null>(null);
  const handleSubToggle = (sub: string) => {
    setSelectedSubs(prev => {
      const next = prev.includes(sub) ? prev.filter(x => x !== sub) : [...prev, sub];
      // If adding a sub and no regions selected, auto-select the parent region
      if (!prev.includes(sub) && selectedRegions.length === 0) {
        const parent = subToRegion.get(sub);
        if (parent) {
          setSelectedRegions([parent]);
          autoSelectedRegionRef.current = parent;
        }
      }
      // If removing a sub and an auto-selected region has no remaining subs, clear it
      if (prev.includes(sub) && autoSelectedRegionRef.current) {
        const regionSubs = regionToSubs.get(autoSelectedRegionRef.current);
        const hasRemaining = next.some(s => regionSubs?.has(s));
        if (!hasRemaining) {
          setSelectedRegions([]);
          autoSelectedRegionRef.current = null;
        }
      }
      return next;
    });
  };

  // Clean up selectedSubs when region filter removes them
  const effectiveSubs = selectedSubs.filter(s => availableSubs.includes(s));
  const allCloseQs = [...new Set(mergedRequests.map(r => r.opp_close_q).filter(Boolean))].sort();
  const mergedFiltered = mergedRequests.filter(r => {
    if (searchId && !r.opp_id.toLowerCase().includes(searchId.toLowerCase())) return false;
    if (searchAccount && !r.acc_name.toLowerCase().includes(searchAccount.toLowerCase())) return false;
    if (searchSc && !r.mergedSc.toLowerCase().includes(searchSc.toLowerCase())) return false;
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.displayStatus)) return false;
    if (selectedRegions.length > 0 && !selectedRegions.includes(r.opp_region)) return false;
    if (effectiveSubs.length > 0 && !effectiveSubs.includes(r.req_sub_region)) return false;
    if (r.req_age !== null && (r.req_age < ageMin || r.req_age > ageMax)) return false;
    if (r.opp_gnarr !== null && (r.opp_gnarr < gnarrMin || r.opp_gnarr > gnarrMax)) return false;
    if (selectedCloseQs.length > 0 && !selectedCloseQs.includes(r.opp_close_q)) return false;
    if (selectedSolution) {
      const requestSolution = bucketSolution(r.req_products || '');
      if (selectedSolution === 'Others') {
        if (!othersLabels.includes(requestSolution)) return false;
      } else if (requestSolution !== selectedSolution) {
        return false;
      }
    }
    return true;
  });
  const STAGE_COLORS: Record<string, {
    bg: string;
    fg: string;
  }> = {
    'Prospect': {
      bg: '#F3F4F6',
      fg: '#6B7280'
    },
    'Qualification': {
      bg: '#EFF6FF',
      fg: '#3B82F6'
    },
    'Influence': {
      bg: '#F0F9FF',
      fg: '#0EA5E9'
    },
    'Solutioning': {
      bg: '#F5F3FF',
      fg: '#8B5CF6'
    },
    'Customer Commit': {
      bg: '#FFF7ED',
      fg: '#F97316'
    },
    'Order': {
      bg: '#FFFBEB',
      fg: '#D97706'
    },
    'Closed - Booked': {
      bg: '#F0FDF4',
      fg: '#16A34A'
    }
  };
  const statusBadge = (status: string) => {
    const c = STAGE_COLORS[status] || {
      bg: '#F9FAFB',
      fg: '#374151'
    };
    return <span className="badge" style={{
      background: c.bg,
      color: c.fg,
      fontSize: 8
    }} data-id="element-1086" data-mp="2k25vq0qa59jl">{status}</span>;
  };
  const mergedColumns: SortableColumn<MergedRequest>[] = [{
    key: 'opp_id',
    label: 'DR ID',
    width: '7%',
    sortValue: r => r.opp_id,
    render: r => <span style={{
      fontSize: 9,
      color: '#8892a0'
    }} data-id="element-1087" data-mp="ppz7a08dkw9c">{r.opp_id}</span>
  }, {
    key: 'acc_name',
    label: 'Account',
    width: '16%',
    sortValue: r => r.acc_name,
    render: r => <span title={r.acc_name} data-id="element-1088" data-mp="2wd6mbdw1y1m4">{r.acc_name.substring(0, 24)}</span>
  }, {
    key: 'mergedSc',
    label: 'SC Assigned',
    width: '12%',
    sortValue: r => r.mergedSc,
    render: r =>
    // td defaults to nowrap + clip; force wrapping so every SC name is visible
    <span title={r.mergedSc} style={{
      display: 'block',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere'
    }} data-id="element-1089" data-mp="2rlf4psuvj684">
        {r.mergedSc.split(',').map(s => s.trim()).filter(Boolean).join(', ')}
      </span>
  }, {
    key: 'displayStatus',
    label: 'Status',
    width: '8%',
    sortValue: r => r.displayStatus,
    render: r => statusBadge(r.displayStatus)
  }, {
    key: 'req_offering',
    label: 'Offering',
    width: '11%',
    sortValue: r => r.req_offering,
    render: r => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-1090" data-mp="28ncbf1vamn4i">
        {(r.req_offering || '').replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
      </span>
  }, {
    key: 'req_age',
    label: 'Age',
    width: '10%',
    sortValue: r => Math.max(...r.allRequests.map(req => req.req_age ?? 0)),
    render: r => <span style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px'
    }} data-id="element-1091" data-mp="22xsvpx2n1vth">
        {r.allRequests.map((req, idx) => {
        const severity = ageSeverity(req.req_age);
        return <a key={req.req_id} href={getRequestURL(req)} target="_blank" rel="noopener noreferrer" className={severity === 'high' ? 'c-red' : severity === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: severity === 'high' ? 600 : 400,
          textDecoration: 'none',
          color: severity === 'high' ? '#DC2626' : severity === 'medium' ? '#D97706' : '#344054'
        }} title={`${req.req_name} - ${formatReqAge(req.req_age)}`} data-id="element-1092" data-mp="1twbjipscqea2" data-mpi={idx}>
              {formatReqAge(req.req_age)}{idx < r.allRequests.length - 1 ? ',' : ''}
            </a>;
      })}
      </span>
  }, {
    key: 'opp_gnarr',
    label: 'GNARR',
    width: '9%',
    sortValue: r => r.opp_gnarr,
    render: r => <>{fmtG(r.opp_gnarr)}</>
  }, {
    key: 'opp_close_q',
    label: 'Close Q',
    width: '8%',
    sortValue: r => r.opp_close_q,
    render: r => <>{r.opp_close_q}</>
  }, {
    key: 'opp_region',
    label: 'Region',
    width: '7%',
    sortValue: r => r.opp_region,
    render: r => regionBadge(r.opp_region)
  }, {
    key: 'req_sub_region',
    label: 'Sub',
    width: '5%',
    sortValue: r => r.req_sub_region,
    render: r => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-1093" data-mp="2p8172ihh5kt5">{r.req_sub_region}</span>
  }];
  const agedColumns: SortableColumn<Request>[] = [{
    key: 'acc_name',
    label: 'Account',
    width: '30%',
    sortValue: r => r.acc_name,
    render: r => <span title={r.acc_name} data-id="element-1094" data-mp="2y5lb1z6q1v7g">{r.acc_name.substring(0, 22)}</span>
  }, {
    key: 'user_sc_name',
    label: 'SC',
    width: '20%',
    sortValue: r => r.user_sc_name,
    render: r => <span title={r.user_sc_name} data-id="element-1095" data-mp="26fgw4g1qe0dm">{r.user_sc_name.split(' ')[0]}</span>
  }, {
    key: 'req_age',
    label: 'Age',
    width: '12%',
    sortValue: r => r.req_age ?? 0,
    render: r => <span className="c-red" style={{
      fontWeight: 600
    }} data-id="element-1096" data-mp="3ds7t9hd2z1ph">{formatReqAge(r.req_age)}</span>
  }, {
    key: 'opp_gnarr',
    label: 'GNARR',
    width: '18%',
    sortValue: r => r.opp_gnarr,
    render: r => <>{fmtG(r.opp_gnarr)}</>
  }, {
    key: 'opp_close_q',
    label: 'Close Q',
    width: '12%',
    sortValue: r => r.opp_close_q,
    render: r => <>{r.opp_close_q}</>
  }, {
    key: 'req_sub_region',
    label: 'Sub',
    width: '8%',
    sortValue: r => r.req_sub_region,
    render: r => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-1097" data-mp="2uuf1nb7wqe7e">{r.req_sub_region}</span>
  }];
  return <div data-id="element-1098" data-mp="2pwd81wcuuwho">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(7, 1fr)'
    }} data-id="element-1099" data-mp="33ulbmn4ecw1t">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-1100" data-mp="33sgx7b8vgena" /> 
        <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-1101" data-mp="2zvcfjblvwm33" />
        <MetricCard label="Aged >30 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-1102" data-mp="j6fd20pcnlaw" />
        <MetricCard label="Closed Booked (DX) GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-1103" data-mp="f53j5izg0dww" />
        <MetricCard label="Closed Booked (Total) GNARR" value={fmtG(wonGnarrTotal)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-1104" data-mp="rmxvhjel5ae3" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-1105" data-mp="hmdu8gr2g5vv" />
        <GnarrInfluencedCard value={oppGnarrTotal} colorClass="c-green" label="GNARR (Total) Influenced" data-id="element-1106" data-mp="15crp0f11ciu5" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-1107" data-mp="1ab2vcfs4gb7m">
        <div className="card" style={{
        minHeight: 260
      }} data-id="element-1108" data-mp="vmyap69idpm7">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4
        }} data-id="element-1109" data-mp="vmf42lpb6nnh">
            <h3 style={{
            margin: 0
          }} data-id="element-1110" data-mp="10k85l1fo2thf">Requests by solution</h3>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 9,
            color: '#667085'
          }} data-id="element-1111" data-mp="1oakot8gd4cnb">
              <span data-id="element-1112" data-mp="1t34367mvjbt6">Top</span>
              <input type="range" min={1} max={offeringTotal} value={offeringTop} onChange={e => setOfferingTop(Number(e.target.value))} style={{
              width: 80,
              accentColor: '#3B82F6'
            }} data-id="element-1113" data-mp="1t4f4m0suk7h4" />
              <span style={{
              minWidth: 20,
              textAlign: 'right'
            }} data-id="element-1114" data-mp="3gcdmq7hh9njv">{offeringTop + 1}</span>
            </div>
          </div>
          <OfferingChart requests={all} top={offeringTop} onTopChange={setOfferingTop} onTotalChange={setOfferingTotal} selected={selectedSolution} onSelect={handleSolutionClick} onOthersChange={setOthersLabels} data-id="element-1115" data-mp="3bfdfht8w6er6" />
        </div>
        <div className="card" style={{
        minHeight: 260,
        maxHeight: 280,
        overflow: 'auto'
      }} data-id="element-1116" data-mp="2wfo5y6lb9ah">
          <h3 data-id="element-1117" data-mp="3u41vc0siptw3">Aged requests (&gt;30 days) at risk</h3>
          {aged.length > 0 ? <SortableTable columns={agedColumns} data={aged} rowKey={r => `${r.opp_id}`} defaultSort={{
          key: 'req_age',
          dir: 'desc'
        }} data-id="element-1118" data-mp="2npapdiojfiv7" /> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-1119" data-mp="28naxlw1u212m">No aged requests in this filter ✓</div>}
        </div>
      </div>

      {/* Opportunities — full list */}
      <SectionHeader title={<>
          Opportunities — full list
          {selectedSolution && <span onClick={() => setSelectedSolution(null)} style={{
        marginLeft: 8,
        fontSize: 9,
        fontWeight: 600,
        color: '#3B82F6',
        cursor: 'pointer',
        textTransform: 'none',
        letterSpacing: 'normal'
      }} data-id="element-1121" data-mp="2rbzfe950rqa6">
              · {selectedSolution} ✕
            </span>}
        </>} data-id="element-1120" data-mp="2iht418ymwaq4" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }} data-id="element-1122" data-mp="3675m6ybxvt4k">
        <input type="text" placeholder="Search DR ID…" value={searchId} onChange={e => setSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 120,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1123" data-mp="16xywd084r19j" />
        <input type="text" placeholder="Search Account…" value={searchAccount} onChange={e => setSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1124" data-mp="3oyw2r2e1i99e" />
        <input type="text" placeholder="Search SC…" value={searchSc} onChange={e => setSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1125" data-mp="bkst0ihjo79d" />
        <div ref={statusDropdownRef} style={{
        position: 'relative',
        zIndex: statusDropdownOpen ? 10 : undefined
      }} data-id="element-1126" data-mp="2idbooztm3scb">
          <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1127" data-mp="261j1q9mf4yig">
            {selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : 'Status ▾'}
          </button>
          {statusDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-1128" data-mp="2jrmlmtxtsg4q">
              <div onClick={() => setSelectedStatuses(selectedStatuses.length === allStatuses.length ? [] : [...allStatuses])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1129" data-mp="14aug38b536z0">
                {selectedStatuses.length === allStatuses.length ? 'Deselect all' : 'Select all'}
              </div>
              {allStatuses.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1130" data-mp="10vsy5k0h72bv">
                  <input type="checkbox" checked={selectedStatuses.includes(s)} onChange={() => {
              setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
            }} data-id="element-1131" data-mp="d5gghhffhr83" />
                  {s}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-1132" data-mp="3gnbd493h0c08">
          <span style={{
          minWidth: 50
        }} data-id="element-1133" data-mp="3umujawcq1d9w">Age</span>
          <RangeSlider min={dataAgeMin} max={dataAgeMax} low={ageMin} high={ageMax} onChangeLow={setAgeMin} onChangeHigh={setAgeMax} width={100} data-id="element-1134" data-mp="3uonpkajf9vhd" />
          <span style={{
          fontSize: 9,
          color: '#8892a0',
          minWidth: 70
        }} data-id="element-1135" data-mp="8fcnk5cugx7n">{ageMin}–{ageMax}d</span>
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-1136" data-mp="3hkxzhzo9n6m">
          <span style={{
          minWidth: 50
        }} data-id="element-1137" data-mp="h74uw76knq80">GNARR</span>
          <RangeSlider min={dataGnarrMin} max={dataGnarrMax} low={gnarrMin} high={gnarrMax} onChangeLow={setGnarrMin} onChangeHigh={setGnarrMax} width={100} data-id="element-1138" data-mp="ha0tzyu5oky1" />
          <span style={{
          fontSize: 9,
          color: '#8892a0',
          minWidth: 70
        }} data-id="element-1139" data-mp="r6vugn9zovey">${(gnarrMin / 1e6).toFixed(0)}M–${(gnarrMax / 1e6).toFixed(0)}M</span>
        </div>
        <div ref={closeQDropdownRef} style={{
        position: 'relative',
        zIndex: closeQDropdownOpen ? 10 : undefined
      }} data-id="element-1140" data-mp="169x9rduwcs65">
          <button onClick={() => setCloseQDropdownOpen(!closeQDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1141" data-mp="1a9xlgqqm2p9i">
            {selectedCloseQs.length > 0 ? `Close Q (${selectedCloseQs.length})` : 'Close Q ▾'}
          </button>
          {closeQDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-1142" data-mp="w9wlrem31nvm">
              <div onClick={() => setSelectedCloseQs(selectedCloseQs.length === allCloseQs.length ? [] : [...allCloseQs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1143" data-mp="2cnmq8mh46e5s">
                {selectedCloseQs.length === allCloseQs.length ? 'Deselect all' : 'Select all'}
              </div>
              {allCloseQs.map(q => <label key={q} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1144" data-mp="3egr01inpgoyc">
                  <input type="checkbox" checked={selectedCloseQs.includes(q)} onChange={() => {
              setSelectedCloseQs(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);
            }} data-id="element-1145" data-mp="34jcs67ful8hn" />
                  {q}
                </label>)}
            </div>}
        </div>
        <div ref={regionDropdownRef} style={{
        position: 'relative',
        zIndex: regionDropdownOpen ? 10 : undefined
      }} data-id="element-1146" data-mp="2wztv4x9rreq2">
          <button onClick={() => setRegionDropdownOpen(!regionDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1147" data-mp="2ibgtbjo031nn">
            {selectedRegions.length > 0 ? `Region (${selectedRegions.length})` : 'Region ▾'}
          </button>
          {regionDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-1148" data-mp="35y6w4n86b09b">
              <div onClick={() => setSelectedRegions(selectedRegions.length === allRegions.length ? [] : [...allRegions])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1149" data-mp="1mzhapoyz6xs0">
                {selectedRegions.length === allRegions.length ? 'Deselect all' : 'Select all'}
              </div>
              {allRegions.map(r => <label key={r} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1150" data-mp="zbzsak1lpc1p">
                  <input type="checkbox" checked={selectedRegions.includes(r)} onChange={() => {
              setSelectedRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
            }} data-id="element-1151" data-mp="198cl7fpbnlve" />
                  {r}
                </label>)}
            </div>}
        </div>
        <div ref={subDropdownRef} style={{
        position: 'relative',
        zIndex: subDropdownOpen ? 10 : undefined
      }} data-id="element-1152" data-mp="4zl7n4i9xrtd">
          <button onClick={() => setSubDropdownOpen(!subDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1153" data-mp="91ya9k1q59lx">
            {effectiveSubs.length > 0 ? `Sub (${effectiveSubs.length})` : 'Sub ▾'}
          </button>
          {subDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-1154" data-mp="wslmuiml4net">
              <div onClick={() => setSelectedSubs(effectiveSubs.length === availableSubs.length ? [] : [...availableSubs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1155" data-mp="2h89xa89gdrwp">
                {effectiveSubs.length === availableSubs.length ? 'Deselect all' : 'Select all'}
              </div>
              {availableSubs.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1156" data-mp="30zogw473lbv3">
                  <input type="checkbox" checked={effectiveSubs.includes(s)} onChange={() => handleSubToggle(s)} data-id="element-1157" data-mp="3ony67g3ishqz" />
                  {s}
                </label>)}
            </div>}
        </div>
      </div>
      <div className="tw" data-id="element-1158" data-mp="37pohbzpvl9mc">
        {searchRows ? <table className="tbl" data-id="element-1159" data-mp="3lo4kxhsfyk09">
            <thead data-id="element-1160" data-mp="3lp5ybswxjil5">
              <tr data-id="element-1161" data-mp="3lppi7f654h1t">
                {mergedColumns.map(col => <th key={col.key} style={{
              width: col.width
            }} data-id="element-1162" data-mp="3gos175wv9t1w">{col.label}</th>)}
              </tr>
            </thead>
            <tbody data-id="element-1163" data-mp="3umzu92qs8a7s">{searchRows}</tbody>
          </table> : <SortableTable columns={mergedColumns} data={mergedFiltered} rowKey={r => r.opp_id} pageSize={25} data-id="element-1164" data-mp="3qm66qah94xff" />}
      </div>

    </div>;
}